import { useState } from 'react'
import { Link } from 'react-router'
import { Check, RotateCcw, X } from 'lucide-react'
import type { AttentionItem } from '@/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { useReviewAttention, useSetAttentionStatus } from '@/hooks/use-reviews'

const CATEGORY_LABEL: Record<string, string> = {
  kitchen: 'Kitchen',
  appliances: 'Appliances',
  beds_and_furniture: 'Beds & furniture',
  cleanliness: 'Cleanliness',
  listing_accuracy: 'Listing accuracy',
  bathroom: 'Bathroom',
  lighting: 'Lighting',
  noise: 'Noise',
  outdoor: 'Outdoor',
  value: 'Value',
  house_rules: 'House rules',
  other: 'Other',
}

function Row({ item }: { item: AttentionItem }) {
  const [open, setOpen] = useState(false)
  const set = useSetAttentionStatus()
  const resolved = item.status !== 'open'
  const latest = item.evidence
    .map(e => e.date)
    .filter(Boolean)
    .sort()
    .at(-1)
  return (
    <li className={`border-t border-border py-2.5 ${resolved ? 'opacity-60' : ''}`}>
      <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            aria-expanded={open}
            className={`text-left text-sm font-medium ${resolved ? 'line-through' : 'text-text'}`}
          >
            {item.title}
          </button>
          <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-textMuted">
            <Link to={`/properties/${item.property_id}`} className="text-mint hover:underline">
              {item.property_name}
            </Link>
            <span>· {CATEGORY_LABEL[item.category] ?? item.category}</span>
            <span>
              · {item.evidence.length} review{item.evidence.length === 1 ? '' : 's'}
            </span>
            {latest && <span>· latest {latest}</span>}
            {item.reopened > 0 && <span className="text-amber">· reopened {item.reopened}×</span>}
            {item.status === 'skipped' && <span>· skipped</span>}
            {item.status === 'done' && <span>· done</span>}
          </div>
        </div>
        <div className="flex flex-none gap-1">
          {item.status === 'open' ? (
            <>
              <Button
                size="sm"
                variant="outline"
                aria-label={`Mark "${item.title}" done`}
                disabled={set.isPending}
                onClick={() => set.mutate({ id: item.id, status: 'done' })}
              >
                <Check className="mr-1 h-3.5 w-3.5" /> Done
              </Button>
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Skip "${item.title}"`}
                disabled={set.isPending}
                onClick={() => set.mutate({ id: item.id, status: 'skipped' })}
              >
                <X className="mr-1 h-3.5 w-3.5" /> Skip
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Reopen "${item.title}"`}
              disabled={set.isPending}
              onClick={() => set.mutate({ id: item.id, status: 'open' })}
            >
              <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reopen
            </Button>
          )}
        </div>
      </div>
      {open && (
        <ul className="mt-2 space-y-1.5 pl-3">
          {item.evidence.map((e, i) => (
            <li key={i} className="text-xs text-textMuted">
              <span
                className={`mr-2 font-semibold ${e.rating <= 3 ? 'text-danger' : e.rating === 4 ? 'text-amber' : 'text-mint'}`}
              >
                {e.rating}★
              </span>
              <span className="text-text">“{e.quote}”</span>
              <span className="ml-2">
                {e.source === 'private_note' ? 'private note' : 'review'}
                {e.date ? ` · ${e.date}` : ''}
                {e.reservation_id && (
                  <>
                    {' · '}
                    <Link
                      to={`/reservations/${e.reservation_id}`}
                      className="text-mint hover:underline"
                    >
                      reservation
                    </Link>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

/**
 * The to-do the reviews write for you: every concrete fix guests asked
 * for, extracted nightly from sub-five reviews and private notes, grouped
 * by theme per property. Done / skip here; a later review repeating the
 * point reopens it automatically.
 */
export function AttentionTodo() {
  const query = useReviewAttention()
  const [showResolved, setShowResolved] = useState(false)
  const items = query.data?.items ?? []
  const open = items.filter(i => i.status === 'open')
  const resolved = items.filter(i => i.status !== 'open')

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle>Needs attention</CardTitle>
          <CardDescription>
            What guests asked you to fix, from below-five reviews and private notes. Updated
            nightly; a repeat reopens a done item.
          </CardDescription>
        </div>
        {resolved.length > 0 && (
          <Button size="sm" variant="outline" onClick={() => setShowResolved(s => !s)}>
            {showResolved ? 'Hide' : 'Show'} {resolved.length} resolved
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {query.isLoading && <LoadingSkeleton className="h-24 w-full" />}
        {query.isError && (
          <p className="text-sm text-danger">Couldn&apos;t load attention items.</p>
        )}
        {query.isSuccess && open.length === 0 && (
          <p className="text-sm text-textMuted">
            {query.data.reviews_read === 0
              ? 'Nothing yet: the first nightly run has not read any reviews.'
              : `Nothing open. ${query.data.reviews_read} reviews read so far.`}
          </p>
        )}
        {(open.length > 0 || (showResolved && resolved.length > 0)) && (
          <ul>
            {open.map(it => (
              <Row key={it.id} item={it} />
            ))}
            {showResolved && resolved.map(it => <Row key={it.id} item={it} />)}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
